import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ChevronDown, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, VISA_TYPES, QUALIFICATIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-line bg-white px-4 py-3 text-base text-ink placeholder:text-slate-400 transition-colors focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30";
const selectCls = `${inputCls} appearance-none pr-10`;
const labelCls = "mb-1.5 block font-accent text-xs font-semibold uppercase tracking-widest text-muted";

const SelectField = ({ label, testid, children, ...props }) => (
  <div>
    <label className={labelCls} htmlFor={testid}>{label}</label>
    <div className="relative">
      <select id={testid} data-testid={testid} className={selectCls} required {...props}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  </div>
);

const ConsultForm = ({ defaultCountry = "", onDone }) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "",
    visa_type: "", country: defaultCountry, qualification: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Enquiry sent! Opening WhatsApp…");
      if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank", "noopener");
      onDone?.();
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="success"
            data-testid="consult-success"
            className="flex flex-col items-center py-10 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.1 }}
            >
              <CheckCircle2 className="h-16 w-16 text-gold" strokeWidth={1.5} />
            </motion.div>
            <h3 className="mt-6 font-heading text-2xl font-bold text-navy">Enquiry Received</h3>
            <p className="mt-3 max-w-sm text-base text-muted">
              Our counselors have been emailed your details and will reach out within 24 hours.
              A pre-filled WhatsApp chat has also opened — just hit send there too.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            className="grid grid-cols-1 gap-5 sm:grid-cols-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            aria-label="Consultation enquiry form"
          >
            <div>
              <label className={labelCls} htmlFor="input-name">Full Name *</label>
              <input id="input-name" data-testid="input-name" className={inputCls} required minLength={2}
                placeholder="Your full name" value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="input-phone">Phone *</label>
              <input id="input-phone" data-testid="input-phone" className={inputCls} required type="tel"
                pattern="[0-9+\s-]{8,15}" placeholder="+91 …" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="input-email">Email *</label>
              <input id="input-email" data-testid="input-email" className={inputCls} required type="email"
                placeholder="you@example.com" value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="input-location">Your Location *</label>
              <input id="input-location" data-testid="input-location" className={inputCls} required
                placeholder="City, State" value={form.location} onChange={set("location")} />
            </div>
            <SelectField label="Visa You're Looking For *" testid="select-visa-type" value={form.visa_type} onChange={set("visa_type")}>
              <option value="" disabled>Select visa type</option>
              {VISA_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
            </SelectField>
            <SelectField label="Destination Country *" testid="select-country" value={form.country} onChange={set("country")}>
              <option value="" disabled>Select country</option>
              {COUNTRIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </SelectField>
            <div className="sm:col-span-2">
              <SelectField label="Highest Qualification *" testid="select-qualification" value={form.qualification} onChange={set("qualification")}>
                <option value="" disabled>Select qualification</option>
                {QUALIFICATIONS.map((q) => <option key={q} value={q}>{q}</option>)}
              </SelectField>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="textarea-notes">Additional Notes</label>
              <textarea id="textarea-notes" data-testid="textarea-notes" rows={3} className={`${inputCls} resize-none`}
                placeholder="Preferred intake, budget, questions…" value={form.notes} onChange={set("notes")} />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="consult-submit-button"
                disabled={submitting}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 font-accent text-sm font-semibold uppercase tracking-widest text-white transition-all duration-300 hover:bg-gold hover:text-navy hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  <>Send Enquiry <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>
                )}
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">
                Your enquiry is emailed to our counselors and opens a pre-filled WhatsApp chat.
              </p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConsultForm;
